"use client";

import {
  createContext,
  useContext,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import { CcContactAntiqueHeroBackdrop } from "@/components/contact/CcContactAntiqueHeroBackdrop";
import { useCcScrollSpeedParallax } from "@/lib/use-cc-scroll-speed-parallax";

/** Slight scroll drift on the antique hero (desktop). */
const ANTIQUE_PARALLAX_SPEED = 0.88;
type CcContactParallaxLayerRefs = {
  formBottomFadeRef: RefObject<HTMLDivElement | null>;
};

const CcContactParallaxLayerRefsContext =
  createContext<CcContactParallaxLayerRefs | null>(null);

/** Bottom fade over scrollable fields (consent / CTA stay above). */
export function CcContactParallaxFormFade() {
  const layerRefs = useContext(CcContactParallaxLayerRefsContext);
  if (!layerRefs) {
    return null;
  }

  const { formBottomFadeRef } = layerRefs;

  return (
    <div
      ref={formBottomFadeRef}
      className="cc-contact__parallax-bottom-fade cc-contact__parallax-bottom-fade--on-form"
      aria-hidden="true"
    />
  );
}

type CcContactParallaxShellProps = {
  children: ReactNode;
};

export function CcContactParallaxShell({ children }: CcContactParallaxShellProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const antiqueRef = useRef<HTMLDivElement>(null);
  const formBottomFadeRef = useRef<HTMLDivElement>(null);

  useCcScrollSpeedParallax(antiqueRef, {
    speed: ANTIQUE_PARALLAX_SPEED,
    triggerRef: frameRef,
    intensity: 0.35,
  });

  const layerRefs: CcContactParallaxLayerRefs = {
    formBottomFadeRef,
  };

  return (
    <CcContactParallaxLayerRefsContext.Provider value={layerRefs}>
      <div ref={frameRef} className="cc-contact__frame cc-contact__frame--parallax">
        <CcContactAntiqueHeroBackdrop
          mediaRef={antiqueRef}
          showAttachedFooterSeam
        />
        {children}
      </div>
    </CcContactParallaxLayerRefsContext.Provider>
  );
}
